import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogContent,
  Stack,
  Chip,
} from '@mui/material';
import {
  Refresh,
  Fullscreen
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import MiniMeterChart from './MiniMeterChart';
import MeterFactorChart from './MeterFactorChart';

interface BatchMeterData {
  meters: {
    [key: string]: {
      data: Array<{
        timestamp: string;
        meter_factor: number;
        ucl: number;
        lcl: number;
        center: number;
      }>;
      statistics: {
        current_health: 'green' | 'yellow' | 'red';
        trend: 'increasing' | 'decreasing' | 'stable';
        anomaly_count: number;
        days_since_proving: number;
      };
    };
  };
  aggregate: {
    total_meters: number;
    meters_in_control: number;
    meters_warning: number;
    meters_critical: number;
    fleet_health_score: number;
    average_deviation: number;
  };
}

const MeterChartsGrid: React.FC = () => {
  const { getIdTokenClaims } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [batchData, setBatchData] = useState<BatchMeterData | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<string>('all');
  const [refreshing, setRefreshing] = useState(false);
  const [fullChartDialog, setFullChartDialog] = useState<{
    open: boolean;
    meterProductId?: string;
    meterId?: string;
    productType?: string;
  }>({ open: false });

  const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'https://fg0jf2txt9.execute-api.us-east-2.amazonaws.com/prod';

  // Fetch batch meter data
  const fetchBatchData = useCallback(async () => {
    try {
      setError(null);
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      const response = await axios.get(
        `${API_ENDPOINT}/meters/batch-history`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        }
      );

      // Handle API Gateway wrapped response
      let result;
      if (response.data.body) {
        result = typeof response.data.body === 'string'
          ? JSON.parse(response.data.body)
          : response.data.body;
      } else {
        result = response.data;
      }

      setBatchData(result);
    } catch (err: any) {
      console.error('Error fetching batch meter data:', err);
      setError(err.message || 'Failed to load meter charts');
    } finally {
      setLoading(false);
    }
  }, [API_ENDPOINT, getIdTokenClaims]);

  useEffect(() => {
    fetchBatchData();
  }, [fetchBatchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchBatchData();
    setRefreshing(false);
  };

  const handleMiniChartClick = (meterProductId: string) => {
    const [meterId, productType] = meterProductId.split('_');
    setFullChartDialog({
      open: true,
      meterProductId,
      meterId,
      productType
    });
  };

  // Get unique product types
  const getProductTypes = () => {
    if (!batchData) return ['all'];
    const products = new Set<string>();
    products.add('all');
    Object.keys(batchData.meters).forEach(key => {
      const productType = key.split('_')[1];
      if (productType) products.add(productType);
    });
    return Array.from(products);
  };

  // Filter meters by product type
  const getFilteredMeters = () => {
    if (!batchData) return {};
    if (selectedProduct === 'all') return batchData.meters;

    return Object.entries(batchData.meters).reduce((acc, [key, value]) => {
      const productType = key.split('_')[1];
      if (productType === selectedProduct) {
        acc[key] = value;
      }
      return acc;
    }, {} as typeof batchData.meters);
  };

  // Calculate aggregate stats for filtered meters
  const getFilteredAggregateStats = () => {
    if (!batchData) return null;
    if (selectedProduct === 'all') return batchData.aggregate;

    const filteredMeters = getFilteredMeters();
    const meterIds = Object.keys(filteredMeters);

    let inControl = 0;
    let warning = 0;
    let critical = 0;

    meterIds.forEach(id => {
      const stats = filteredMeters[id].statistics;
      switch (stats.current_health) {
        case 'green': inControl++; break;
        case 'yellow': warning++; break;
        case 'red': critical++; break;
      }
    });

    return {
      total_meters: meterIds.length,
      meters_in_control: inControl,
      meters_warning: warning,
      meters_critical: critical,
      fleet_health_score: meterIds.length > 0
        ? Math.round((inControl / meterIds.length) * 100)
        : 0,
      average_deviation: batchData.aggregate.average_deviation
    };
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Meter Factor Control Charts
            </Typography>
            <CircularProgress size={24} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  const productTypes = getProductTypes();
  const filteredMeters = getFilteredMeters();
  const aggregateStats = getFilteredAggregateStats();

  return (
    <>
      <Card>
        <CardContent>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {/* Summary Stats */}
            {aggregateStats && (
              <Stack direction="row" spacing={1} sx={{ mr: 2 }}>
                <Chip
                  label={`${aggregateStats.meters_in_control} In Control`}
                  size="small"
                  color="success"
                />
                <Chip
                  label={`${aggregateStats.meters_warning} Warning`}
                  size="small"
                  color="warning"
                />
                <Chip
                  label={`${aggregateStats.meters_critical} Critical`}
                  size="small"
                  color="error"
                />
                <Chip
                  label={`Health: ${aggregateStats.fleet_health_score.toFixed(1)}%`}
                  size="small"
                  variant="outlined"
                />
              </Stack>
            )}

          </Box>

          {/* Product Tabs */}
          <Tabs
            value={selectedProduct}
            onChange={(_, value) => setSelectedProduct(value)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            {productTypes.map(product => (
              <Tab
                key={product}
                label={product === 'all' ? 'All Products' : product}
                value={product}
              />
            ))}
          </Tabs>

          {/* Charts Grid */}
          <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(1, 1fr)',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(4, 1fr)',
                  xl: 'repeat(5, 1fr)'
                },
                gap: 2
              }}
            >
              {Object.entries(filteredMeters).map(([meterProductId, meterData]) => {
                const [meterId, productType] = meterProductId.split('_');
                return (
                  <MiniMeterChart
                    key={meterProductId}
                    meterProductId={meterProductId}
                    meterId={meterId}
                    productType={productType}
                    data={meterData.data}
                    statistics={meterData.statistics}
                    onClick={() => handleMiniChartClick(meterProductId)}
                  />
                );
              })}
            </Box>

          {/* No Data Message */}
          {Object.keys(filteredMeters).length === 0 && (
            <Alert severity="info">
              No meter data available for the selected product type.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Full Chart Dialog */}
      <Dialog
        open={fullChartDialog.open}
        onClose={() => setFullChartDialog({ open: false })}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent>
          {fullChartDialog.meterProductId && (
            <MeterFactorChart
              meterProductId={fullChartDialog.meterProductId}
              meterName={fullChartDialog.meterId}
              productType={fullChartDialog.productType}
              onClose={() => setFullChartDialog({ open: false })}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MeterChartsGrid;